import type { AlignmentData, BrowseResponse, InspectionData, Measurement, MeasurementType, Note, NoteCategory, Point3D, Project } from "../types.ts";

const BASE = "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, init);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? detail;
    } catch {
      // ignore JSON parse error
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  projects: {
    list: (): Promise<Project[]> => req("/projects"),
    get: (id: string): Promise<Project> => req(`/projects/${id}`),
    delete: (id: string): Promise<void> => req(`/projects/${id}`, { method: "DELETE" }),
    rename: (id: string, name: string): Promise<Project> =>
      req(`/projects/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }),
    assetUrl: (id: string, role: "glb" | "ply" | "floorplan" | "potree_manifest" | "rooms_data" | "roomplan_model") =>
      `${BASE}/projects/${id}/assets/${role}`,
  },

  upload: (
    file: File,
    name: string,
    onProgress?: (pct: number) => void,
  ): Promise<{ project_id: string; status: string }> => {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BASE}/upload`);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          let detail = xhr.statusText;
          try {
            detail = JSON.parse(xhr.responseText).detail ?? detail;
          } catch {
            // ignore
          }
          reject(new Error(detail));
        }
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(form);
    });
  },

  measurements: {
    list: (projectId: string): Promise<Measurement[]> => req(`/measurements/${projectId}`),
    create: (data: {
      project_id: string;
      type: MeasurementType;
      points: Point3D[];
      value?: number;
      unit?: string;
      label?: string;
    }): Promise<Measurement> =>
      req("/measurements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (id: string): Promise<void> => req(`/measurements/${id}`, { method: "DELETE" }),
  },

  notes: {
    list: (projectId: string): Promise<Note[]> => req(`/notes/${projectId}`),
    create: (data: {
      project_id: string;
      category: NoteCategory;
      text: string;
      point: Point3D;
    }): Promise<Note> =>
      req("/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (id: string): Promise<void> => req(`/notes/${id}`, { method: "DELETE" }),
  },

  debug: {
    status: (): Promise<{ enabled: boolean }> => req("/debug/status"),
    logsUrl: (projectId: string): string => `${BASE}/debug/logs/${projectId}`,
  },

  streetview: {
    manifestUrl: (projectId: string) => `${BASE}/projects/${projectId}/streetview/manifest`,
    imageUrl: (projectId: string, imageId: number) =>
      `${BASE}/projects/${projectId}/streetview/images/${imageId}.jpg`,
  },

  pano360: {
    manifestUrl: (projectId: string) => `${BASE}/projects/${projectId}/pano360/manifest`,
    imageBaseUrl: (projectId: string) => `${BASE}/projects/${projectId}/pano360/images`,
    align: (
      projectId: string,
      nodeId: number,
      body: { position: { x: number; y: number; z: number }; yaw: number; aligned: boolean },
    ): Promise<{ status: string; node_id: number }> =>
      req(`/projects/${projectId}/pano360/align/${nodeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  },

  inspection: {
    load: (projectId: string): Promise<InspectionData> => req(`/projects/${projectId}/inspection`),
    mediaUrl: (projectId: string, filename: string) =>
      `${BASE}/projects/${projectId}/inspection/media/${encodeURIComponent(filename)}`,
  },

  alignment: {
    get: async (projectId: string): Promise<AlignmentData | null> => {
      const res = await fetch(`${BASE}/projects/${projectId}/alignment`);
      if (res.status === 204) return null;
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    },
    save: (projectId: string, data: AlignmentData): Promise<AlignmentData> =>
      req(`/projects/${projectId}/alignment`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    delete: (projectId: string): Promise<void> =>
      req(`/projects/${projectId}/alignment`, { method: "DELETE" }),
  },

  quickbase: {
    lookup: (folderId: string): Promise<{ fields: any[]; record: Record<string, any> }> =>
      req(`/quickbase/lookup?folder_id=${encodeURIComponent(folderId)}`),
  },

  gdrive: {
    browse: (folderId?: string): Promise<BrowseResponse> => {
      const params = folderId ? `?folder_id=${folderId}` : "";
      return req(`/gdrive/browse${params}`);
    },
    streamUrl: (fileId: string): string => `${BASE}/gdrive/stream/${fileId}`,
    open: (
      fileId: string,
      fileName: string,
      folderPath: string,
    ): Promise<{ project_id: string; status: string }> =>
      req("/gdrive/open", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_id: fileId, file_name: fileName, folder_path: folderPath }),
      }),
  },
};
